import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';
import { Users, DollarSign, UserCheck, AlertTriangle, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const styles = {
  page: {
    padding: '2rem',
    maxWidth: 1200,
    margin: '0 auto',
  },
  heading: {
    fontFamily: 'Playfair Display, serif',
    color: 'var(--text)',
    fontSize: '2rem',
    marginBottom: '0.25rem',
  },
  subtitle: {
    fontFamily: 'DM Sans, sans-serif',
    color: 'var(--secondary)',
    fontSize: '0.95rem',
    marginBottom: '2rem',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1.25rem',
    marginBottom: '2rem',
  },
  kpiCard: {
    background: 'var(--card-bg)',
    borderRadius: 12,
    padding: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  kpiIcon: {
    width: 48,
    height: 48,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  kpiValue: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '1.75rem',
    color: 'var(--text)',
    lineHeight: 1.1,
  },
  kpiLabel: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '0.8rem',
    color: 'var(--secondary)',
    marginTop: 2,
  },
  chartCard: {
    background: 'var(--card-bg)',
    borderRadius: 12,
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  chartTitle: {
    fontFamily: 'Playfair Display, serif',
    color: 'var(--text)',
    fontSize: '1.1rem',
    marginBottom: '1rem',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  },
  nbaPanel: {
    background: 'var(--card-bg)',
    borderRadius: 12,
    padding: '1.5rem',
  },
  nbaItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid rgba(242,237,228,0.06)',
    fontFamily: 'DM Sans, sans-serif',
    color: 'var(--text)',
    fontSize: '0.9rem',
  },
  nbaBadge: {
    fontSize: '0.7rem',
    padding: '3px 10px',
    borderRadius: 20,
    fontWeight: 600,
  },
};

const TIER_COLORS = ['var(--secondary)', 'var(--primary)', 'var(--alert)'];

const fallbackTrend = Array.from({ length: 24 }, (_, i) => {
  const date = new Date(2024, i, 1);
  return {
    month: date.toLocaleString('default', { month: 'short', year: '2-digit' }),
    acv: Math.round(960 + i * 15 + Math.random() * 40),
  };
});

const fallbackTierData = [
  { name: 'Seedling', value: 55, color: 'var(--secondary)' },
  { name: 'Bloom', value: 30, color: 'var(--primary)' },
  { name: 'Harvest', value: 15, color: 'var(--alert)' },
];

const fallbackNBA = [
  { customer: 'Maria L.', action: 'Send win-back offer', model: 'Churn Risk', urgency: 'High' },
  { customer: 'Jake T.', action: 'Upgrade to oat milk latte', model: 'Upsell', urgency: 'Medium' },
  { customer: 'Priya S.', action: 'Push: matcha cross-sell', model: 'Cross-Sell', urgency: 'Medium' },
  { customer: 'Carlos R.', action: 'Replenishment reminder', model: 'Replenishment', urgency: 'Low' },
  { customer: 'Emma W.', action: '$10 toward Harvest tier', model: 'Near Harvest', urgency: 'High' },
  { customer: 'Liam K.', action: 'SMS: new seasonal menu', model: 'Campaign', urgency: 'Low' },
  { customer: 'Aisha B.', action: 'Loyalty milestone reward', model: 'Rising Star', urgency: 'Medium' },
  { customer: 'Noah D.', action: 'Re-engage: 18 days inactive', model: 'Churn Risk', urgency: 'High' },
  { customer: 'Olivia M.', action: 'Bundle: cold brew + granola', model: 'Basket', urgency: 'Low' },
  { customer: 'Wei Z.', action: 'VIP preview invite', model: 'VIP Likely', urgency: 'Medium' },
];

export default function Dashboard() {
  const [stats, setStats] = useState({ totalCustomers: 1200, avgOrderValue: 15.14, activeMembers: 847, atRisk: 156 });
  const [trendData, setTrendData] = useState(fallbackTrend);
  const [tierData] = useState(fallbackTierData);
  const [nbaData] = useState(fallbackNBA);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [statsRes, trendsRes] = await Promise.all([
          api.get('/customers/stats').catch(() => null),
          api.get('/transactions/trends').catch(() => null),
        ]);
        if (statsRes?.data) {
          const d = statsRes.data;
          setStats({
            totalCustomers: d.totalCustomers || 1200,
            avgOrderValue: d.avgOrderValue || d.avgOrderValue || 15.14,
            activeMembers: d.activeMembers30d || d.activeMembers || 847,
            atRisk: d.atRiskCount || d.atRisk || 156,
          });
        }
        if (trendsRes?.data?.length) {
          const mapped = trendsRes.data.map(d => ({
            month: d.month,
            acv: Math.round(d.revenue || d.acv || 0),
          }));
          setTrendData(mapped);
        }
      } catch {
        // use fallback data
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const kpis = [
    { label: 'Total Customers', value: stats.totalCustomers?.toLocaleString() || '1,200', icon: <Users size={22} />, bg: 'rgba(141,184,122,0.15)', color: 'var(--primary)' },
    { label: 'Avg Order Value', value: `$${(stats.avgOrderValue || 14.20).toFixed(2)}`, icon: <DollarSign size={22} />, bg: 'rgba(200,169,110,0.15)', color: 'var(--secondary)' },
    { label: 'Active Members (30d)', value: (stats.activeMembers || 847).toLocaleString(), icon: <UserCheck size={22} />, bg: 'rgba(141,184,122,0.15)', color: 'var(--primary)' },
    { label: 'At-Risk Customers', value: (stats.atRisk || 156).toLocaleString(), icon: <AlertTriangle size={22} />, bg: 'rgba(212,107,58,0.15)', color: 'var(--alert)' },
  ];

  const urgencyColor = { High: 'var(--alert)', Medium: 'var(--secondary)', Low: 'var(--primary)' };

  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>Dashboard</h1>
      <p style={styles.subtitle}>Folio Coffee Co. — real-time customer intelligence</p>

      <div style={styles.kpiGrid}>
        {kpis.map((k) => (
          <div key={k.label} style={styles.kpiCard}>
            <div style={{ ...styles.kpiIcon, background: k.bg, color: k.color }}>{k.icon}</div>
            <div>
              <div style={styles.kpiValue}>{k.value}</div>
              <div style={styles.kpiLabel}>{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.chartCard}>
        <div style={styles.chartTitle}>ACV Trend — 24 Months</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,237,228,0.08)" />
            <XAxis dataKey="month" tick={{ fill: 'var(--secondary)', fontSize: 11, fontFamily: 'DM Sans' }} />
            <YAxis tick={{ fill: 'var(--secondary)', fontSize: 11, fontFamily: 'DM Sans' }} />
            <Tooltip
              contentStyle={{ background: 'var(--card-bg)', border: '1px solid rgba(242,237,228,0.1)', borderRadius: 8, fontFamily: 'DM Sans', color: 'var(--text)' }}
            />
            <Line type="monotone" dataKey="acv" stroke="var(--primary)" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={styles.row}>
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Customers by Tier</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={tierData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,237,228,0.08)" />
              <XAxis type="number" tick={{ fill: 'var(--secondary)', fontSize: 11, fontFamily: 'DM Sans' }} unit="%" />
              <YAxis dataKey="name" type="category" tick={{ fill: 'var(--text)', fontSize: 12, fontFamily: 'DM Sans' }} width={80} />
              <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid rgba(242,237,228,0.1)', borderRadius: 8, fontFamily: 'DM Sans', color: 'var(--text)' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {tierData.map((entry, i) => (
                  <Cell key={i} fill={TIER_COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.nbaPanel}>
          <div style={styles.chartTitle}>Next Best Actions</div>
          {nbaData.map((item, i) => (
            <div key={i} style={styles.nbaItem}>
              <div>
                <strong>{item.customer}</strong> — {item.action}
                <span style={{ color: 'var(--secondary)', fontSize: '0.75rem', marginLeft: 8 }}>{item.model}</span>
              </div>
              <span style={{ ...styles.nbaBadge, background: `${urgencyColor[item.urgency]}22`, color: urgencyColor[item.urgency] }}>
                {item.urgency}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
